import { Box, InputAdornment, TextField as MuiTextField, Typography } from "@mui/material";
import FilterIcon from "../../../assets/filter_icon.svg";
import PdfIcon from "../../../assets/pdf_icon.svg";
import XlsIcon from "../../../assets/xls_icon.svg";
import MainLayout from "../../../layouts/main/main_layout";
import SchemeLayout from "../../../layouts/schemes/scheme_layout";
import { useEffect, useRef, useState } from "react";
import DataGridFamilyEnquiries from "../components/data_grid/data_grid_family_enquiries";
import { useQuery } from "react-query";
import {
  downloadEnquiries,
  getEnquiredSchemes,
} from "../../../services/schemes/schemes";
import { getMasterData } from "../../../services/master/master_service";
import { downloadFromBlob } from "../../../utils/utils";
import Loading from "../../../components/loading/loading";
import FilterFamilyEnquiries from "../components/filter_family_enquries/filter_family_enquiries";
import { PaginationState } from "../enquiries/enquiries_list";
import DownloadPopup from "../components/download_popup/download_popup";
import { RECORDS_EXCEEDS_ALERT_MSG } from "../../../constants";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import SortIcon from "../../../assets/sort.svg";
import { NeedFilters } from "../../needs/model/needs_filters";
import { getNeedsFilters } from "../../../services/needs/needs_service";
import { SearchRounded } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import { useActiveFamilyFilterCount, useFamilyEnquiriesFilterStore } from "../../../store/use-scheme-family-enquiries-filter-store";
import { greyEnabled, purple } from "../../../theme/colors";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import OrgSelector from "../../../components/org_selector/org_selector";
import SortEnquiriesPopup, {
  DEFAULT_ENQUIRIES_SORT_OPTION,
  EnquiriesSortOption,
  enquiriesSortOptionLabel,
  enquiriesSortOptionToParams,
} from "../components/sort_enquiries_popup/sort_enquiries_popup";

export interface FamilyEnquiryLabels {
  label: string;
}

export interface MasterSchemeInterface {
  id: number;
  value: string;
  is_aggregate: boolean;
}

const enquiryLabels: FamilyEnquiryLabels[] = [
  {
    label: "Ref.No",
  },
  {
    label: "Primary Username",
  },
  {
    label: "Primary User Contact Number",
  },
  {
    label: "Scheme Enquired",
  },
  {
    label: "Family Member Name",
  },
  {
    label: "Relationship with Primary User",
  },
  {
    label: "Enquired On",
  },
  {
    label: "Notification Status",
  },
];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function FamilyEnquiryList() {
  const { showSnackbar } = useFeedback();
  const [searchParams, setUrlParam] = useSearchParams();
  const [page, setPage] = useState(
    searchParams?.has("page") ? parseInt(searchParams.get("page")!) : 1
  );
  const [openDownloadUsersPopup, setOpenDownloadPopup] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("");
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);
  const [sortOption, setSortOption] = useState<EnquiriesSortOption>(DEFAULT_ENQUIRIES_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const activeFilterCount = useActiveFamilyFilterCount();
  const { selectedOrgId } = useSelectedOrgStore();


  const {
    openEnquiriesModal,
    setOpenEnquiriesModal,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    schemeKeyword,
    setSchemeKeyword,
    stateId,
    setStateId,
    categoryId,
    setCategoryId,
    districtId,
    setDistrictId,
    gender,
    setGender,
    ageFrom,
    setAgeFrom,
    ageTo,
    setAgeTo,
    governmentType,
    setGovernmentType,
    resetFilter,
    searchVal,
    setSearchValue,
  } = useFamilyEnquiriesFilterStore();
  


  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchVal);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchVal);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchVal]);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    setPage(1);
  }, [selectedOrgId, sortOption]);

  const { data: familyEnquiryList, isLoading } = useQuery(
  [
    "familyEnquiry",
    page,
    sortOption,
    debouncedSearchValue,
    startDate,
    endDate,
    schemeKeyword,
    stateId,
    categoryId,
    gender,
    districtId,
    ageFrom,
    ageTo,
    governmentType,
    selectedOrgId,
  ],
  async () => {
    try {
      const { sortBy, sortOrder } = enquiriesSortOptionToParams(sortOption);
      let response = await getEnquiredSchemes({
        type: "family",
        dateFrom: startDate,
        dateTo: endDate,
        page: page,
        size: 8,
        schemeKeyword: schemeKeyword,
        state: stateId,
        category: categoryId,
        sortBy,
        sortOrder,
        gender: gender,
        districtId: districtId,
        ageFrom: ageFrom,
        ageTo: ageTo,
        keyword: debouncedSearchValue,
        governmentTypeId: governmentType,
        orgId: selectedOrgId,
      });

      return response?.data?.data?.data || { list: [], total: 0 };
    } catch (error: any) {
      console.error("Error fetching family enquiries:", error);
      return { list: [], total: 0 };
    }
  },
  {
    retry: false,
  }
);


  // Distinct key from create_user/modify_user's "masterDataList" (a
  // different getMasterData with a different response shape) - sharing a
  // key across two different endpoints let one page's fetch clobber the
  // cache for the other. See users.tsx for the bug this caused.
  const { data: masterDataList } = useQuery(["masterSchemesDataList"], async () => {
    let response = await getMasterData();
    return response.data.data;
  });

  const downloadEnquiriesApiCall = (
    fromPage?: number | null,
    toPage?: number | null
  ) => {
    if ((toPage! + 1 - fromPage!) * 10 <= 1000) {
      const { sortBy, sortOrder } = enquiriesSortOptionToParams(sortOption);
      setDownloadSubmitting(true);
      downloadEnquiries({
        type: "family",
        dateFrom: startDate,
        dateTo: endDate,
        schemeKeyword: schemeKeyword,
        state: stateId,
        category: categoryId,
        format: downloadFormat,
        page: fromPage!,
        size: (toPage! + 1 - fromPage!) * 10,
        sortBy,
        sortOrder,
        gender: gender,
        districtId: districtId,
        ageFrom: ageFrom,
        ageTo: ageTo,
        governmentTypeId: governmentType,
        orgId: selectedOrgId,
      })
        .then((res) => {
          let extension = downloadFormat === "pdf" ? ".pdf" : ".xlsx";
          downloadFromBlob(res, new Date().valueOf() + extension);
        })
        .catch((e) => {
          showSnackbar(
            e?.response?.data?.error?.message ||
              "Something went wrong while generating the report."
          );
        })
        .finally(() => setDownloadSubmitting(false));
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
    }
    setOpenDownloadPopup(false);
  };

  useEffect(() => {
    const newPage = (page).toString();
    setUrlParam({ page: newPage }, { replace: true });

  }, [page, setUrlParam]);

  return (
    <MainLayout>
      <SchemeLayout page={page} setPage={setPage}>
        <Box
          display="flex"
          flexDirection={"column"}
          mt={4}
          sx={{ height: "100%" }}
        >
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            width="100%"
            px={2}
            py={2}
          >
            <Box maxWidth={{ xs: "100%", sm: 420 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
              <TextField
                value={searchVal}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                size="small"
                fullWidth
                placeholder="Search by name, contact number or scheme"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1.5}>
              <OrgSelector lockToOwnOrg />
              <ToolbarButton
                icon={XlsIcon}
                label="Export XLS"
                onClick={() => {
                  setOpenDownloadPopup(true);
                  setDownloadFormat("excel");
                }}
                loading={downloadSubmitting && downloadFormat === "excel"}
                disabled={downloadSubmitting}
              />
              <ToolbarButton
                icon={PdfIcon}
                label="Export PDF"
                onClick={() => {
                  setOpenDownloadPopup(true);
                  setDownloadFormat("pdf");
                }}
                loading={downloadSubmitting && downloadFormat === "pdf"}
                disabled={downloadSubmitting}
              />
              <ToolbarButton
                icon={SortIcon}
                label={enquiriesSortOptionLabel(sortOption)}
                onClick={() => setSortDialogOpen(true)}
              />
              <ToolbarButton
                icon={FilterIcon}
                label="Filters"
                onClick={() => setOpenEnquiriesModal(true)}
                badgeCount={activeFilterCount}
              />
            </Box>
          </Box>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent={"center"}
              alignItems={"center"}
              height="100%"
            >
              <Loading />
            </Box>
          ) : (
            <DataGridFamilyEnquiries
              labels={enquiryLabels}
              data={familyEnquiryList?.list || []}
              count={familyEnquiryList?.total || 0}
              page={page}
              setPage={setPage}
            />
          )}

          <SortEnquiriesPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            title="Sort Family Enquiries"
            sortOption={sortOption}
            setSortOption={setSortOption}
          />

          <FilterFamilyEnquiries
            govermentList={masterDataList?.schemeGovernment || []}
            states={masterDataList?.state || []}
            category={masterDataList?.schemeCategory || []}
            genderList={masterDataList?.gender}
          />
        </Box>
        {openDownloadUsersPopup && (
          <DownloadPopup
            onDialogClose={() => {
              setOpenDownloadPopup(false);
            }}
            open={openDownloadUsersPopup}
            format={downloadFormat === "pdf" ? "pdf" : "excel"}
            submitting={downloadSubmitting}
            key={1}
            onSubmit={(fromPage: number | null, toPage: number | null) => {
              downloadEnquiriesApiCall(fromPage, toPage);
            }}
          />
        )}
      </SchemeLayout>
    </MainLayout>
  );
}

